import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { alpha, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import constants from "../../assets/constants";
import { createNewUser } from "../../data/omniDataModel";
import * as PartnerSessionActions from "../../state/redux/partnerSession";
import * as PracticeSessionActions from "../../state/redux/practiceSession";
import { capitalizeAllWords, getAgeFromDateString, isValidUUID } from "../../utils/helpers";
import { deepCopyObject } from "../../utils/helpers";
import UserDialog from "../dialogs/userDialog";
import OmniAvatar from "../ui/omniAvatar";
import OmniEditButton from "../ui/omniEditButton";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget";
import WidgetList from "../ui/widgetList";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const PracticePartners = props => {
  const { relatedPartnerODMs } = props;

  const [selectedPartner, setSelectedPartner] = useState(null);

  const getPartnerInitials = partnerODM => {
    let theReturn =
      partnerODM.omniData.givenName.charAt(0) + partnerODM.omniData.familyName.charAt(0);

    return theReturn.toUpperCase();
  };

  const getPartnerDescription = partnerODM => {
    let theReturn = "";
    if (partnerODM.omniData.partnerData.race != null) {
      theReturn += capitalizeAllWords(partnerODM.omniData.partnerData.race);
    }

    if (partnerODM.omniData.partnerData.ethnicity != null) {
      if (theReturn.length > 0) {
        theReturn += "  ";
      }
      theReturn += capitalizeAllWords(partnerODM.omniData.partnerData.ethnicity);
    }
    if (partnerODM.omniData.partnerData.gender != null) {
      if (theReturn.length > 0) {
        theReturn += "  ";
      }
      theReturn += capitalizeAllWords(partnerODM.omniData.partnerData.gender);
    }

    if (theReturn.length > 0) {
      theReturn += ",  ";
    }
    theReturn += `Age ${getAgeFromDateString(partnerODM.omniData.partnerData.birthdate)}`;

    return theReturn;
  };

  const loadPartnerImage = partnerId => {
    try {
      //TODO call DB here to get data retrieval
      let image = `${
        window.location.origin.includes("localhost")
          ? `https://dev.omnisciahealth.coach`
          : window.location.origin
      }/assets/demo/images/userImage(3).jpg`;

      if (partnerId === constants.demoUserId.userTwo) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(1).jpg`;
      }
      if (partnerId === constants.demoUserId.userThree) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(22).jpg`;
      }
      if (partnerId === constants.demoUserId.userFour) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(11).jpg`;
      }
      return image;
    } catch (error) {
      return `${
        window.location.origin.includes("localhost")
          ? `https://dev.omnisciahealth.coach`
          : window.location.origin
      }/assets/demo/images/userImage(3).jpg`;
    }
  };

  const navigateToManagePortal = () => {
    props.history.push(constants.appPaths.appPathAdminPartner);
  };

  const [titleText, setTitleText] = useState(null);

  useEffect(() => {
    if (relatedPartnerODMs != null) {
      setTitleText(
        `${relatedPartnerODMs.length} Teammate${relatedPartnerODMs.length === 1 ? "" : "s"}`,
      );
    } else setTitleText("");
  }, [relatedPartnerODMs]);

  const theme = useTheme();

  return (
    <Widget>
      <WidgetTitle title={"Partners"} text={titleText} loading={relatedPartnerODMs == null}>
        <OmniResponsiveButton
          tooltipTitle="Add a partner to this practice"
          buttonText="Partner"
          onClick={() => {
            setSelectedPartner(createNewUser(false, false, true));
          }}
        />
      </WidgetTitle>

      {relatedPartnerODMs == null && <WidgetSkeleton />}
      {relatedPartnerODMs != null && (
        <WidgetList>
          {relatedPartnerODMs
            .sort((a, b) => a.omniData.familyName.localeCompare(b.omniData.familyName))
            .map((partner, index) => {
              return (
                <ListItem
                  key={`partner${index}`}
                  disableGutters={true}
                  sx={{
                    padding: theme.spacing(1),
                    paddingTop: 0,
                    paddingBottom: 0,
                    marginBottom: theme.spacing(1),
                    borderRadius: theme.spacing(1),
                    backgroundColor: alpha(theme.palette.info.main, 0.75),
                    color: theme.palette.info.contrastText,
                    cursor: "pointer",
                    boxShadow: theme.shadows[3],
                    userSelect: "none",
                    webkitUserSelect: "none",
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedPartner(partner);
                  }}
                >
                  {!isValidUUID(partner._id) && (
                    <Tooltip title={"No Notes"} placement="right">
                      <OmniAvatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: "1.8rem",
                          border: `2px solid ${theme.palette.info.contrastText}`,
                        }}
                        src={loadPartnerImage(partner._id)}
                      ></OmniAvatar>
                    </Tooltip>
                  )}

                  {isValidUUID(partner._id) && (
                    <Tooltip title={"No Notes"} placement="right">
                      <OmniAvatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: "1.2rem",
                          color: theme.palette.info.main,
                          border: `2px solid ${theme.palette.info.contrastText}`,
                        }}
                      >
                        {getPartnerInitials(partner)}
                      </OmniAvatar>
                    </Tooltip>
                  )}
                  <ListItemText
                    primary={
                      <Typography
                        variant="h6"
                        sx={{
                          padding: theme.spacing(1),
                          paddingTop: 0,
                          paddingBottom: 0,
                          marginBottom: theme.spacing(1),
                          color: theme.palette.info.contrastText,
                          margin: 0,
                          cursor: "pointer",
                          userSelect: "none",
                          webkitUserSelect: "none",
                        }}
                      >
                        {partner.omniData.displayName
                          ? partner.omniData.displayName
                          : `${partner.omniData.givenName} ${partner.omniData.familyName}`}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          marginLeft: theme.spacing(1),
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          marginTop: "-6px",
                          color: theme.palette.info.contrastText,
                          minimumHeight: "20px",
                        }}
                      >
                        {getPartnerDescription(partner)}
                      </Typography>
                    }
                  />
                  <OmniEditButton
                    title={"Manage"}
                    onClick={e => {
                      e.stopPropagation();
                      props.setPartnerODMRequest(partner);
                      navigateToManagePortal();
                    }}
                    icon={
                      <OpenInNewIcon
                        sx={{
                          height: 42,
                          width: 42,
                        }}
                      />
                    }
                  />
                </ListItem>
              );
            })}
        </WidgetList>
      )}

      {selectedPartner && (
        <UserDialog
          open={selectedPartner != null}
          dialogTitle={`${selectedPartner._id == null ? "Add" : "Edit"} Partner`}
          omniODM={deepCopyObject(selectedPartner)}
          onDelete={partner => {
            props.deletePracticePartnerRequest(partner._id);
            setSelectedPartner(null);
          }}
          onAccept={partner => {
            props.updatePracticePartnerRequest(partner);
            setSelectedPartner(null);
          }}
          onClose={() => {
            setSelectedPartner(null);
          }}
        />
      )}
    </Widget>
  );
};
const mapStateToProps = state => ({
  relatedPartnerODMs: state.practiceSession.relatedPartnerODMs,
});

const mapDispatchToProps = {
  deletePracticePartnerRequest: PracticeSessionActions.deletePracticePartnerRequest,
  updatePracticePartnerRequest: PracticeSessionActions.updatePracticePartnerRequest,
  setPartnerODMRequest: PartnerSessionActions.setPartnerODMRequest,
  // loadPartnerODMRequest: PartnerSessionActions.loadPartnerODMRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PracticePartners));
